<template>
    <div class="flex w-full flex-col">
        <!-- Display items -->
        <div v-if="modelValue?.length > 0" class="flex flex-col flex-wrap gap-1 mb-1">
            <div
                v-for="(item, index) in modelValue"
                :key="index"
                class="flex items-center justify-between rounded bg-slate-800 py-1.5 pl-2.5 pr-1 text-sm"
            >
                <div class="flex">
                    <AppIcon :name="config.icon" class="my-auto mr-0.5 h-3.5 w-3.5 text-slate-400" />
                    <span class="text-white">{{ item.id }}</span>
                </div>

                <div class="mr-1.5 flex">
                    <div
                        class="my-auto cursor-pointer rounded-sm p-0.5 text-white opacity-80 hover:bg-black hover:bg-opacity-20 hover:opacity-70"
                        @click="removeItem(index)"
                    >
                        <AppIcon name="xmark" class="h-4 w-4" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Input field -->
        <div class="relative flex w-full flex-col">
            <div class="relative">
                <input
                    v-model="inputValue"
                    type="text"
                    :placeholder="config.placeholder"
                    :class="[
                        'flex h-10 w-full rounded border bg-background px-3 py-2 text-base text-sm outline-none placeholder:text-slate-400 hover:border-slate-300 focus:border-slate-300 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50',
                        isValid ? 'border-slate-200' : 'border-red-300 bg-red-50'
                    ]"
                    @keydown="handleKeyDown"
                    @input="sanitizeInput"
                    @focus="isFocused = true"
                    @blur="isFocused = false"
                />

                <div class="absolute right-0 top-0 flex h-full px-2" @click="addItem">
                    <div
                        class="m-auto cursor-pointer rounded p-1 text-slate-500 bg-white hover:bg-slate-100 hover:text-slate-600"
                    >
                        <AppIcon name="plus" class="h-4 w-4" />
                    </div>
                </div>
            </div>

            <div
                :class="isFocused ? 'opacity-100' : 'opacity-0'"
                class="text-right text-xs transition-opacity duration-150 mt-0.5"
            >Hit <span class="underline">enter</span> to save your {{ config.itemName }}</div>

            <div v-if="!isValid && inputValue && config.validator" class="mt-1 text-xs text-red-600">
                {{ config.validationMessage }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// Default configurations for different input types
const DEFAULT_CONFIGS = {
    hashtag: {
        icon: 'hashtag',
        itemName: 'hashtag',
        placeholder: 'Type and press space or enter to add hashtag...',
        sanitizeChars: '#',
        allowedChars: /[^a-zA-Z0-9_]/g,
        validator: null,
        validationMessage: '',
        caseTransform: 'toLowerCase'
    },
    mention: {
        icon: 'at',
        itemName: 'mention',
        placeholder: 'Type and press space or enter to add mention...',
        sanitizeChars: '@',
        allowedChars: null, // No character filtering for mentions
        validator: (value) => {
            if (!value || value.trim() === '') return true
            const trimmed = value.trim()
            // Instagram usernames: 1-30 characters, letters, numbers, periods, underscores
            const instagramUsernameRegex = /^[a-zA-Z0-9_]([a-zA-Z0-9_.]*[a-zA-Z0-9_])?$/
            return trimmed.length >= 1 &&
                   trimmed.length <= 30 &&
                   instagramUsernameRegex.test(trimmed) &&
                   !trimmed.includes('..')
        },
        validationMessage: 'Please enter a valid username (1-30 characters, letters, numbers, periods, underscores)',
        caseTransform: null // Keep original case
    }
}

// Props
const props = defineProps({
    modelValue: {
        type: Array,
        default: () => [],
    },
    type: {
        type: String,
        default: 'hashtag',
        validator: (value) => ['hashtag', 'mention'].includes(value)
    },
    placeholder: {
        type: String,
        default: null,
    },
    maxItems: {
        type: Number,
        default: 50,
    },
    config: {
        type: Object,
        default: () => ({})
    },
    icon: {
        type: Array,
        default: null,
        validator: (value) => !value || (Array.isArray(value) && value.length === 2)
    }
})

// Emits
const emit = defineEmits(['update:modelValue'])

const inputValue = ref('')
const isFocused = ref(false)

// Merge default config with custom config
const config = computed(() => {
    const baseConfig = DEFAULT_CONFIGS[props.type] || DEFAULT_CONFIGS.hashtag
    const customPlaceholder = props.placeholder || baseConfig.placeholder
    const customIcon = props.icon || baseConfig.icon
    return {
        ...baseConfig,
        ...props.config,
        placeholder: customPlaceholder,
        icon: customIcon
    }
})

// Validate input based on config
const isValid = computed(() => {
    if (!config.value.validator) return true
    return config.value.validator(inputValue.value)
})

// Sanitize input based on config
const sanitizeInput = () => {
    // Remove configured characters
    if (config.value.sanitizeChars) {
        const regex = new RegExp(`[${config.value.sanitizeChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`, 'g')
        inputValue.value = inputValue.value.replace(regex, '')
    }

    // Filter allowed characters
    if (config.value.allowedChars) {
        inputValue.value = inputValue.value.replace(config.value.allowedChars, '')
    }
}

const addItem = () => {
    let value = inputValue.value.trim()

    // Apply case transformation if specified
    if (config.value.caseTransform === 'toLowerCase') {
        value = value.toLowerCase()
    } else if (config.value.caseTransform === 'toUpperCase') {
        value = value.toUpperCase()
    }

    // Check validation
    if (config.value.validator && !config.value.validator(value)) {
        return // Don't add invalid items
    }

    // Check if the item already exists (case-insensitive)
    const isDuplicate = props.modelValue.some((item) => item.id.toLowerCase() === value.toLowerCase())

    if (value && !isDuplicate && props.modelValue.length < props.maxItems) {
        // Create a new array with the new item object and emit the update
        const updatedItems = [...props.modelValue, { id: value }]
        emit('update:modelValue', updatedItems)
        inputValue.value = ''
    } else if (isDuplicate) {
        // Clear the input if it's a duplicate
        inputValue.value = ''
    }
}

const removeItem = (index) => {
    // Create a new array without the removed item
    const updatedItems = [...props.modelValue]
    updatedItems.splice(index, 1)
    emit('update:modelValue', updatedItems)
}

const handleKeyDown = (event) => {
    // Add item on Enter or Space or Tab
    if ((event.key === 'Enter' || event.key === ' ' || event.key === 'Tab') && inputValue.value.trim()) {
        event.preventDefault()
        addItem()
    }
}
</script>
